import { useState } from "react";
import { X, GitMerge } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Admin modal — manually re-parent a member in the binary tree.
 * POSTs to /admin/users/{code}/move-placement and records the move in the
 * placement-moves audit log.
 */
export default function PlacementMoveModal({ member, onClose, onSaved }) {
  const [newParent, setNewParent] = useState("");
  const [side, setSide] = useState("L");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!member) return null;

  const submit = async () => {
    if (!newParent.trim()) return toast.error("Enter the new parent code");
    if (!window.confirm(`Move ${member.user_code} under ${newParent.trim().toUpperCase()} (${side === "L" ? "Left" : "Right"})?\nThis is audit-logged.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${member.user_code}/move-placement`, {
        new_parent_code: newParent.trim(),
        side,
        reason: reason.trim(),
      });
      toast.success("Placement moved — audit logged");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="placement-move-modal"
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm"
      onClick={() => !busy && onClose?.()}
    >
      <div
        className="w-full max-w-lg bg-white rounded-sm border border-border shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GitMerge size={16} className="text-royal" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-royal">Manual Placement Move</div>
              <h3 className="font-serif text-lg text-navy">{member.name} · <span className="font-mono text-sm">{member.user_code}</span></h3>
            </div>
          </div>
          <button onClick={onClose} className="text-navy/50 hover:text-navy p-1.5"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <div className="bg-cream/50 border border-line rounded-sm px-4 py-3 text-xs text-navy/70">
            Current: <b className="font-mono text-navy">{member.parent_code || member.placement_parent_code || "—"}</b> · Side <b>{member.placement || "—"}</b>
          </div>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest text-navy/50">New parent user code</span>
            <input
              type="text"
              value={newParent}
              onChange={(e) => setNewParent(e.target.value.toUpperCase())}
              data-testid="move-new-parent"
              placeholder="e.g. SM100005"
              className="mt-1 w-full border border-line rounded-sm px-3 py-2 font-mono tracking-wider focus:border-royal outline-none"
            />
          </label>
          <div>
            <span className="block text-[10px] uppercase tracking-widest text-navy/50">Side</span>
            <div className="mt-1 flex gap-2">
              {[
                { k: "L", l: "Left" },
                { k: "R", l: "Right" },
              ].map((o) => (
                <button
                  key={o.k}
                  type="button"
                  data-testid={`move-side-${o.k}`}
                  onClick={() => setSide(o.k)}
                  className={`flex-1 px-4 py-2 text-xs uppercase tracking-widest border rounded-sm transition-colors ${
                    side === o.k ? "bg-royal text-white border-royal" : "border-line text-navy hover:border-royal/40"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest text-navy/50">Reason (optional, shows in audit log)</span>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="move-reason"
              placeholder="Legitimate placement adjustment, grievance resolution, etc."
              className="mt-1 w-full border border-line rounded-sm px-3 py-2 focus:border-royal outline-none"
            />
          </label>
        </div>
        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="text-sm text-navy/60 hover:text-navy px-3 py-2">Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="move-submit"
            className="text-sm bg-royal text-white hover:bg-royal-deep px-5 py-2 rounded-sm disabled:opacity-50"
          >
            {busy ? "Moving…" : "Move placement"}
          </button>
        </div>
      </div>
    </div>
  );
}
